import { BasicSelect, LocalizedValue } from '@agro-club/frontend-shared'
import useLangPicker from 'hooks/useLangPicker'
import { TargetSkuTypes } from 'modules/domain/target2sku/common/types'
import { endpoints } from 'modules/endpoints'
import React, { useMemo } from 'react'
import { EntitySelect, EntitySelectProps } from 'views/components/EntitySelect/EntitySelect'
import { createGetList } from './common'

export type TargetSkuProduct = {
  id: string
  title: LocalizedValue
}

type TargetSkuProductsFilter = {
  manufacturer_id?: string
  subcategory_id?: string
  territory_id?: string
  only_current_season?: boolean
  title?: string
}

type TargetSkuProductSelectProps = EntitySelectProps<TargetSkuProduct> & {
  subcategoryId?: string
  manufacturerId?: string
  territoryId?: string
  onlyCurrentSeason?: boolean
  valueLabel?: string
  scope?: TargetSkuTypes | 'default' | 'pedigreed'
}

const getListForm = createGetList<TargetSkuProduct, TargetSkuProductsFilter>(endpoints.target2sku('products'))

const getListTerritoryFilter = createGetList<TargetSkuProduct, TargetSkuProductsFilter>(
  endpoints.target2sku('territory/product'),
)

const getListRetailerFilter = createGetList<TargetSkuProduct, TargetSkuProductsFilter>(
  endpoints.target2sku('retailer/product'),
)

const getListFarmerFilter = createGetList<TargetSkuProduct, TargetSkuProductsFilter>(
  endpoints.target2sku('farmer/product'),
)

const getListCropFilter = createGetList<TargetSkuProduct, TargetSkuProductsFilter>(
  endpoints.target2sku('territory-crop/product'),
)

const getListPedigreedFilter = createGetList<TargetSkuProduct, TargetSkuProductsFilter>(
  endpoints.target2sku('pedigreed-territory/product'),
)

const getListPedigreedGrowerFilter = createGetList<TargetSkuProduct, TargetSkuProductsFilter>(
  endpoints.target2sku('pedigreed-grower/product'),
)

const getPedigreedFormList = createGetList<TargetSkuProduct, TargetSkuProductsFilter>(
  endpoints.target2sku('pedigreed-products'),
)

const scopeManagerMap = {
  default: getListForm,
  pedigreed: getPedigreedFormList,
  [TargetSkuTypes.Farmer]: getListFarmerFilter,
  [TargetSkuTypes.Retailer]: getListRetailerFilter,
  [TargetSkuTypes.Territory]: getListTerritoryFilter,
  [TargetSkuTypes.Crop]: getListCropFilter,
  [TargetSkuTypes.Pedigreed]: getListPedigreedFilter,
  [TargetSkuTypes.PedigreedGrower]: getListPedigreedGrowerFilter,
}

const getValue = (item: TargetSkuProduct) => item.id

export const TargetSkuProductSelect: React.VFC<TargetSkuProductSelectProps> = ({
  subcategoryId,
  manufacturerId,
  territoryId,
  onlyCurrentSeason,
  isDisabled,
  valueLabel,
  onChange,
  scope = 'default',
  ...props
}) => {
  const { pick } = useLangPicker()

  const filter = useMemo(
    () => ({
      manufacturer_id: manufacturerId,
      subcategory_id: subcategoryId,
      territory_id: territoryId,
      only_current_season: onlyCurrentSeason || undefined,
    }),
    [manufacturerId, subcategoryId, territoryId, onlyCurrentSeason],
  )
  const fakeValue = useMemo(() => (valueLabel ? { title: { en: valueLabel }, id: 'fake' } : undefined), [valueLabel])

  const getLabel = (item: TargetSkuProduct) => pick(item.title)

  const testId = 'target-product-select'

  return isDisabled ? (
    <BasicSelect
      isDisabled
      getOptionLabel={getLabel}
      getOptionValue={getValue}
      {...props}
      value={fakeValue}
      data-test-id={testId}
    />
  ) : (
    <EntitySelect
      filter={filter}
      getEntityList={scopeManagerMap[scope]}
      getOptionLabel={getLabel}
      getOptionValue={getValue}
      onChange={onChange}
      searchParamKey="title"
      data-test-id={testId}
      {...props}
    />
  )
}
